import { RoutePath } from '../../../constants/routePaths';

export const DCM_PAGES = {
  ACCOUNT: 'dcm.account',
  ADMIN: 'dcm.admin',
  AD_GALLERY: 'dcm.ad-gallery',
  CAMPAIGNS: 'dcm.campaigns',
  CAMPAIGN_CREATE: 'dcm.campaign-create',
  FORBIDDEN: 'dcm.forbidden',
  FORGOT_PASSWORD: 'dcm.forgot-password',
  HOME: 'dcm.home',
  LOGIN: 'dcm.login',
  LINE_ITEMS: 'dcm.line-items',
  PERFORMANCE: 'dcm.performance',
  LOGOUT: 'dcm.logout',
  NOT_ASSIGNED: 'dcm.not-assigned',
  NOT_FOUND: 'dcm.not-found',
  ONBOARDING: 'dcm.onboarding',
  ORG_SWITCHER: 'dcm.org-switcher',
  REPORTS: 'dcm.reports',
  RESEND_INVITE: 'dcm.resend-invite',
  SET_PASSWORD: 'dcm.set-password',
  SUMMARY: 'dcm.summary',
  NOTIFICATIONS: 'dcm.notifications',
  MAINTENANCE: 'dcm.maintenance',
  CAMPAIGN_BUILDER: 'dcm.campaign-builder',
  CAMPAIGN_REVIEW: 'dcm.campaign-review',
  CAMPAIGN_PAYMENT: 'dcm.campaign-payment',
  CAMPAIGN_SUCCESS: 'dcm.campaign-success',
  CAMPAIGN_SUMMARY: 'dcm.campaign-summary',
  LINE_ITEM_SUMMARY: 'dcm.line-item-summary',
  CAMPAIGN_PERFORMANCE: 'dcm.campaign-performance',
  LINE_ITEM_PERFORMANCE: 'dcm.line-item-performance',
  CREATIVE_STUDIO: 'dcm.creative-studio',
  OTHER: 'dcm.other',
} as const;

export const ROUTE_PATH_TO_DCM_PAGE: Record<RoutePath, (typeof DCM_PAGES)[keyof typeof DCM_PAGES]> = {
  [RoutePath.ACCOUNT]: DCM_PAGES.ACCOUNT,
  [RoutePath.ADMIN]: DCM_PAGES.ADMIN,
  [RoutePath.AD_GALLERY]: DCM_PAGES.AD_GALLERY,
  [RoutePath.CAMPAIGNS]: DCM_PAGES.CAMPAIGNS,
  [RoutePath.CAMPAIGN_CREATE]: DCM_PAGES.CAMPAIGN_CREATE,
  [RoutePath.FORBIDDEN]: DCM_PAGES.FORBIDDEN,
  [RoutePath.FORGOT_PASSWORD]: DCM_PAGES.FORGOT_PASSWORD,
  [RoutePath.HOME]: DCM_PAGES.HOME,
  [RoutePath.LOGIN]: DCM_PAGES.LOGIN,
  [RoutePath.LINE_ITEMS]: DCM_PAGES.LINE_ITEMS,
  [RoutePath.PERFORMANCE]: DCM_PAGES.PERFORMANCE,
  [RoutePath.LOGOUT]: DCM_PAGES.LOGOUT,
  [RoutePath.NOT_ASSIGNED]: DCM_PAGES.NOT_ASSIGNED,
  [RoutePath.NOT_FOUND]: DCM_PAGES.NOT_FOUND,
  [RoutePath.ONBOARDING]: DCM_PAGES.ONBOARDING,
  [RoutePath.ORG_SWITCHER]: DCM_PAGES.ORG_SWITCHER,
  [RoutePath.REPORTS]: DCM_PAGES.REPORTS,
  [RoutePath.RESEND_INVITE]: DCM_PAGES.RESEND_INVITE,
  [RoutePath.SET_PASSWORD]: DCM_PAGES.SET_PASSWORD,
  [RoutePath.SUMMARY]: DCM_PAGES.SUMMARY,
  [RoutePath.NOTIFICATIONS]: DCM_PAGES.NOTIFICATIONS,
  [RoutePath.MAINTENANCE]: DCM_PAGES.MAINTENANCE,
};

export const COMPONENT_OUTCOMES = {
  USER: 'user',
  AUTO: 'auto',
  PROGRAMMATIC: 'programmatic',
} as const;

export const CREATION_TYPES = {
  NEW: 'new',
  DUPLICATE: 'duplicate',
} as const;

export const LOGIN_FLOW_FAILURE_REASONS = {
  INVALID_CREDENTIALS: 'dcm.invalid-credentials',
  ORGANIZATIONS_FETCH_ERROR: 'dcm.organizations-fetch-error',
  MISSING_AMS_USER: 'dcm.missing-ams-user',
  ORG_SWITCHER_ERROR: 'dcm.org-switcher-error',
} as const;

/** How a creative was added — sent on Creative Uploaded / Creative Submitted as `upload_source`. */
export const UPLOAD_SOURCES = {
  AD_FILE: 'dcm.ad-file',
  AD_BUILDER: 'dcm.ad-builder',
  AD_GALLERY: 'dcm.ad-gallery',
  CAMPAIGN_PAYMENT: 'dcm.campaign-payment',
  VAST: 'dcm.vast',
} as const;

export const BROWSE_ACTIONS = {
  LEARN_MORE_MODAL: 'dcm.learn-more',
  SEARCH: 'dcm.search',
} as const;

export const REJECTION_SOURCES = {
  UPLOAD_VALIDATION: 'dcm.upload-validation',
  QCHECK: 'dcm.qcheck',
} as const;

export const SESSION_STORAGE_KEYS = {
  LOGIN_FLOW_START: 'dcm_analytics_login_flow_start_ms',
  LOGIN_FLOW_HAD_MFA: 'dcm_analytics_login_flow_had_mfa',
  PENDING_LINE_ITEM_CREATED: 'dcm_analytics_pending_line_item_created',
} as const;

export const CAMPAIGN_BUILDER_STEP_NAMES = {
  SETUP: 'setup',
  CONTENT: 'content',
  TARGETING: 'targeting',
  DELIVERY: 'delivery',
  ADS: 'ads',
} as const;

export const FORM_INTERACTION_TYPES = {
  FOCUS: 'focus',
  BLUR: 'blur',
  CHANGE: 'change',
} as const;

export const EDIT_TYPES = {
  SETUP: 'setup',
  TARGETING: 'targeting',
  DELIVERY: 'delivery',
  ADS: 'ads',
  INVENTORY: 'inventory',
  BUDGET: 'budget',
  DATE: 'date',
  CREATIVE: 'creative',
} as const;

export const POLITICAL_CLASSIFICATION_VALUES = {
  POLITICAL: 'political',
  NON_POLITICAL: 'non-political',
} as const;

export const PRODUCT_IDS = {
  DCM: 'dcm',
} as const;

export const PRODUCT_ENVIRONMENTS = {
  PRODUCTION: 'production',
  NON_PRODUCTION: 'non-production',
} as const;

export const AUTH_STATES = {
  AUTHENTICATED: 'authenticated',
  ANONYMOUS: 'anonymous',
} as const;

export const ERROR_TYPES = {
  GRAPHQL: 'graphql',
  NETWORK: 'network',
  USER: 'user',
} as const;

export const ERROR_SEVERITIES = {
  ERROR: 'error',
  WARNING: 'warning',
} as const;

export const CAMPAIGN_STATUS_VALUES = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  UNDER_REVIEW: 'under-review',
} as const;

export const INVENTORY_UNAVAILABLE_REASONS = {
  NO_INVENTORY: 'no-inventory',
  CHECK_FAILED: 'check-failed',
} as const;

export const SLOW_PAGE_THRESHOLDS_MS = {
  LCP: 2500,
  TTFB: 800,
  FCP: 1800,
  FID: 100,
} as const;

export const SLOW_EXIT_MAX_TIME_ON_PAGE_MS = 10_000;

export const RAGE_CLICK_THRESHOLD = 3;
export const RAGE_CLICK_WINDOW_MS = 800;

/** Amplitude user properties for account onboarding / identity (identify, not event props). */
export const USER_PROPERTY_KEYS = {
  DCM_USER_ACCOUNT_CREATE_DATE: 'dcm_user_account_create_date',
  DCM_ADVERTISER_ACCOUNT_CREATE_DATE: 'dcm_advertiser_account_create_date',
  DCM_BRAND_ACCOUNT_CREATE_DATE: 'dcm_brand_account_create_date',
  ACCOUNT_ACTIVATION_VERIFIED: 'account_activation_verified',
  PROFILE_BILLING_COMPLETE: 'profile_billing_complete',
  ACCOUNT_ORIGINATION_SOURCE: 'account_origination_source',
  USER_LAST_LOGIN_AT: 'user_last_login_at',
} as const;

/** Cleared from the user profile when no ad account is selected. */
export const AD_ACCOUNT_SCOPED_USER_PROPERTY_KEYS = [
  USER_PROPERTY_KEYS.DCM_ADVERTISER_ACCOUNT_CREATE_DATE,
  USER_PROPERTY_KEYS.DCM_BRAND_ACCOUNT_CREATE_DATE,
  USER_PROPERTY_KEYS.ACCOUNT_ORIGINATION_SOURCE,
  USER_PROPERTY_KEYS.PROFILE_BILLING_COMPLETE,
] as const;
